<script setup lang="ts">
import { computed, nextTick, onUnmounted, ref, watch } from "vue";

import actions from "~/actions";
import { getPropertyDisplayTitle, getPropertyPartialTask, getPropertyValueFromTask } from "~/common/properties";
import DropdownMenu from "~/components/dumb/DropdownMenu.vue";
import DropdownMenuItemContent from "~/components/dumb/DropdownMenuItemContent.vue";
import NumberEditorInput from "~/components/dumb/NumberEditorInput.vue";
import ProgressBar from "~/components/dumb/ProgressBar.vue";
import Tooltip from "~/components/dumb/Tooltip.vue";
import { THROTTLE_MS } from "~/constants/app";
import { colorsByTheme } from "~/constants/style";
import { ChevronDownIcon, ChevronUpIcon, NumberFieldIcon, SizeFieldIcon } from "~/icons";
import { DropdownMenuItemKind, EditorMode, NumberFormat, Placement, PropertyKind } from "~/shared/enums";
import type { PropertyDefaultSize, PropertyNumber, PropertyValueForKind, Task } from "~/shared/types";
import { useAppStore, useDataStore, usePageStore } from "~/stores";
import { makeUuid, sanitizeIntegerInput } from "~/utils/common";
import { ThrottleManager } from "~/utils/throttleManager";

type Value = PropertyValueForKind<PropertyNumber | PropertyDefaultSize>;

const props = defineProps<{
  property: PropertyNumber | PropertyDefaultSize;
  tasks: Task[];
  defaultValue?: Value;
  editorMode: EditorMode;
  value?: Value;
}>();

const emit = defineEmits<{
  update: [value?: Value];
}>();

const appStore = useAppStore();
const dataStore = useDataStore();
const pageStore = usePageStore();

const inputRef = ref<HTMLInputElement | null>(null);

const interval = ref<ReturnType<typeof setInterval> | null>(null);

const showEditor = ref(false);
const isFocused = ref(false);

const colors = computed(() => colorsByTheme[pageStore.theme]);
const id = ref(`number-${makeUuid()}`);

const isDollars = computed(() => props.property.adtl.format === NumberFormat.DOLLARS);
const isPercentage = computed(() => props.property.adtl.format === NumberFormat.PERCENTAGE);
const isInteger = computed(() => props.property.adtl.format === NumberFormat.INTEGER);

const numberValue = computed(() => {
  if (props.editorMode === EditorMode.FORM || props.editorMode === EditorMode.AUTOMATION) {
    return props.value ?? null;
  }

  const firstTaskValue = getPropertyValueFromTask(props.property, props.tasks[0]);
  return props.tasks.every((task) => getPropertyValueFromTask(props.property, task) === firstTaskValue)
    ? firstTaskValue
    : null;
});

const isChipMode = computed(() => props.editorMode === EditorMode.CHIP);
const isListMode = computed(() => props.editorMode === EditorMode.LIST);
const isTaskDetailMode = computed(() => props.editorMode === EditorMode.DETAIL);
const isContextMenuMode = computed(() => props.editorMode === EditorMode.CONTEXT_MENU);
const isFormMode = computed(() => props.editorMode === EditorMode.FORM);
const isAutomationMode = computed(() => props.editorMode === EditorMode.AUTOMATION);
const isFormOrAutomationMode = computed(() => isFormMode.value || isAutomationMode.value);

const selectedTasks = computed(() =>
  !isListMode.value
    ? props.tasks
    : dataStore.getTasksByDuidsOrderedIfLoaded([...appStore.selectedTaskDuids], {
        includeTrashed: false,
        includeDraft: false,
      })
);

const save = (value: Value, noBackend: boolean) => {
  dataStore.updateTasks(
    selectedTasks.value.map((task) => ({
      duid: task.duid,
      ...getPropertyPartialTask(props.property, task, value),
    })),
    { noBackend }
  );
};

const numberInner = ref(numberValue.value ?? null);
const hasNumberValue = computed(() => numberInner.value !== null);
const hasPercentageValue = computed(() => isPercentage.value && hasNumberValue.value);
const saveManager = new ThrottleManager(() => {
  save(numberInner.value, false);
}, THROTTLE_MS);

watch(
  () => numberValue.value,
  (value) => {
    if (value === numberInner.value) {
      return;
    }

    numberInner.value = value;
    saveManager.cancel();
  }
);

const descendantTasks = computed(() =>
  props.tasks[0]
    ? dataStore.getTasksByDuidsOrderedIfLoaded(dataStore.getDescendantDuidsUiOnly(props.tasks[0]), {
        includeTrashed: false,
        includeDraft: false,
      })
    : []
);

const hasSubtasks = computed(() => descendantTasks.value.length > 0);

const totalSize = computed(() => {
  if (!hasSubtasks.value || !props.property.adtl.rollup) {
    return numberInner.value;
  }

  const subtaskSum = props.tasks[0] ? props.tasks[0].rollup[props.property.duid] : 0;
  if (typeof subtaskSum !== "number") {
    return numberInner.value;
  }

  const res = isPercentage.value ? subtaskSum / descendantTasks.value.length : subtaskSum;
  if (numberInner.value === null) {
    return res > 0 ? res : null;
  }

  return res + (isPercentage.value ? 0 : numberInner.value);
});

const hasValue = computed(() => numberValue.value !== null || totalSize.value !== null);

const displayedText = computed(() => {
  if (hasSubtasks.value && props.property.adtl.rollup) {
    return `${totalSize.value ?? ""}${numberInner.value !== null ? ` (${numberInner.value})` : ""}`;
  }

  if (isTaskDetailMode.value && numberInner.value === null) {
    return "-";
  }

  return numberInner.value;
});

const showNumberEditor = computed(
  () =>
    isFocused.value ||
    !hasSubtasks.value ||
    !props.property.adtl.rollup ||
    showEditor.value ||
    props.property.kind === PropertyKind.DEFAULT_SIZE ||
    descendantTasks.value.every((task) => getPropertyValueFromTask(props.property, task) === null)
);

const tooltipText = computed(() => {
  const hasValidSubtaskValues = descendantTasks.value.some((task) => {
    const value = getPropertyValueFromTask(props.property, task);
    return value !== null && value !== undefined;
  });

  if (!hasSubtasks.value || !props.property.adtl.rollup || !hasValidSubtaskValues || numberInner.value === null) {
    return [];
  }

  const formatValue = (value: number) => `${isDollars.value ? "$" : ""}${value}${isPercentage.value ? "%" : ""}`;

  return [
    `${formatValue(totalSize.value ?? 0)} including subtasks`,
    `${formatValue(numberInner.value ?? 0)} for this task`,
  ];
});

const onClick = () => {
  actions.visualization.selectRowByIdAndScroll(props.tasks[0].duid);
  showEditor.value = true;
  nextTick(() => inputRef.value?.focus());
};

watch(selectedTasks, (newSelectedTasks) => {
  if (newSelectedTasks.length === 0 && showEditor.value) {
    showEditor.value = false;
  }
});

const updateValue = (value: Value) => {
  numberInner.value = value;
  if (isFormOrAutomationMode.value) {
    emit("update", value);
    return;
  }

  save(value, true);

  emit("update");
  saveManager.run();
};

const dropdownSections = computed(() => [
  {
    title: getPropertyDisplayTitle(props.property),
    items: [
      {
        title: "Edit value",
        kind: DropdownMenuItemKind.COMPONENT,
        noFocus: true,
        component: NumberEditorInput,
        componentArgs: {
          value: numberInner.value,
          property: props.property,
          reducedPadding: true,
          onUpdate: (value: number | null) => {
            updateValue(value);
          },
        },
        onAssignRef: (elem: HTMLElement) => {
          nextTick(() => {
            const input = elem.querySelector("input");
            input?.focus();
          });
        },
      },
    ],
  },
]);

const onInput = (e: Event) => {
  const value = sanitizeIntegerInput(e);
  updateValue(value === "" || value === "-" ? null : parseInt(value, 10));
};

const focus = () => inputRef.value?.focus();

const increment = () => {
  updateValue((numberInner.value ?? 0) + 1);
  focus();
};

const decrement = () => {
  updateValue((numberInner.value ?? 0) - 1);
  focus();
};

const startIncrement = (e: MouseEvent | KeyboardEvent) => {
  e.preventDefault();

  if (!interval.value) {
    increment();
  }
  interval.value = setInterval(increment, 200);
};

const startDecrement = (e: MouseEvent | KeyboardEvent) => {
  e.preventDefault();

  if (!interval.value) {
    decrement();
  }
  interval.value = setInterval(decrement, 200);
};

const stopAction = () => {
  if (interval.value) {
    clearInterval(interval.value);
    interval.value = null;
  }

  saveManager.finish();
};

const onFocus = () => {
  isFocused.value = true;
  showEditor.value = true;
};

const onBlur = () => {
  isFocused.value = false;
  if (isFormOrAutomationMode.value) {
    return;
  }
  if (isTaskDetailMode.value) {
    showEditor.value = false;
  }

  saveManager.finish();
};

onUnmounted(() => {
  saveManager.destroy();
});

defineExpose({
  focus,
});
</script>

<template>
  <DropdownMenu
    v-if="isContextMenuMode"
    :sections="dropdownSections"
    has-input
    mobile-include-back
    :placement="Placement.RIGHT_TOP"
    :style="{ '--background': colors.borderVlt, '--highlight': colors.borderMd }">
    <DropdownMenuItemContent
      :icon="props.property.kind === PropertyKind.NUMBER ? NumberFieldIcon : SizeFieldIcon"
      :title="`Change ${getPropertyDisplayTitle(property).toLowerCase()}`"
      is-submenu />
  </DropdownMenu>
  <Tooltip
    v-else
    :disabled="isFormOrAutomationMode"
    :text="`Change ${getPropertyDisplayTitle(property).toLowerCase()}`"
    :block="isListMode || isFormOrAutomationMode"
    :height-block="isListMode || isFormOrAutomationMode"
    :class="isTaskDetailMode && 'w-full'">
    <Tooltip
      :text="tooltipText"
      :placement="Placement.TOP"
      :disabled="!tooltipText.length || props.property.kind === PropertyKind.DEFAULT_SIZE"
      class="w-full">
      <div
        class="group/number-input relative flex size-full max-w-full items-center rounded-sm select-none"
        :class="{
          'h-[26px] gap-0.5 text-sm hover:bg-opposite/10': isChipMode,
          'border border-oncolor px-1 py-0.5': isChipMode && !hasPercentageValue,
          'px-2 hover:bg-lt': isFormOrAutomationMode || (isTaskDetailMode && !hasPercentageValue),
          'min-h-7 justify-start': isTaskDetailMode,
          'px-1': !isFormOrAutomationMode && !isChipMode,
          'justify-center pb-px': isListMode,
        }">
        <div
          v-if="hasPercentageValue"
          class="absolute inset-0 flex items-center"
          :class="!isChipMode && (isTaskDetailMode ? 'px-2' : 'px-2 py-1.5')">
          <ProgressBar :value="totalSize ?? numberValue ?? 0" />
        </div>
        <div
          v-if="hasPercentageValue && isTaskDetailMode"
          class="pointer-events-none absolute inset-0 mx-2 flex items-center opacity-0 transition-opacity group-hover/number-input:opacity-100">
          <div class="size-full overflow-hidden rounded-sm border border-primary-base" />
        </div>
        <span v-if="isDollars && hasValue" :class="isTaskDetailMode && 'pt-0.5'">$</span>

        <button
          v-if="!showNumberEditor"
          type="button"
          :class="[
            isTaskDetailMode && `w-full py-1 ${hasPercentageValue ? 'mx-2 justify-end pr-6' : 'px-0 text-left'}`,
            isListMode && 'size-full',
            (isListMode || hasPercentageValue) && 'text-right',
            isDollars && hasNumberValue && 'items-start pl-1',
            isListMode && !isPercentage && 'pr-[22px]',
            isPercentage && !isTaskDetailMode && 'pr-[32px]',
            isChipMode && hasPercentageValue && 'w-full justify-end px-1',
          ]"
          class="relative cursor-pointer truncate p-0 text-sm focus-ring-none"
          @click="onClick">
          {{ displayedText }}
        </button>

        <div v-else class="flex size-full items-center" :class="isTaskDetailMode && (isDollars || isInteger) && 'mr-4'">
          <label :for="id" class="sr-only">{{ getPropertyDisplayTitle(property) }}</label>
          <input
            :id="id"
            ref="inputRef"
            v-auto-width="{ disabled: !isChipMode, minWidthPx: 9, maxWidthPx: 270 }"
            type="text"
            inputmode="numeric"
            :value="numberInner"
            :placeholder="
              isListMode
                ? isTaskDetailMode
                  ? 'None'
                  : undefined
                : isAutomationMode
                  ? '(Unset)'
                  : getPropertyDisplayTitle(property)
            "
            :class="[
              isChipMode || isFormOrAutomationMode ? '!border border-hvy focus:border-hvy' : 'size-full',
              (isChipMode || isAutomationMode) && 'h-5',
              isFormMode && 'py-[5px]',
              isFormOrAutomationMode && 'w-full',
              isTaskDetailMode && 'size-full px-0 py-1',
              (isListMode || hasPercentageValue) && 'text-right',
              isDollars && hasNumberValue && 'pl-1',
              isListMode && !hasPercentageValue && 'pr-[22px]',
              hasPercentageValue && (isChipMode ? 'ml-0.5 pr-0' : 'ml-1.5 pr-[14px]'),
            ]"
            class="relative cursor-text truncate border-none bg-transparent p-0 text-sm focus-ring-none placeholder:text-vlt"
            @mousedown.stop
            @keydown.stop
            @focus="onFocus"
            @blur="onBlur"
            @input="onInput" />
        </div>

        <div
          class="relative flex items-center select-none"
          :class="!isChipMode ? '!absolute inset-y-0 right-1.5' : hasPercentageValue && 'px-1'">
          <span v-if="hasPercentageValue" class="pb-px" :class="!showNumberEditor && 'pr-3.5'">%</span>
          <div
            v-if="showNumberEditor"
            class="flex-col justify-center pl-0.5 opacity-0 group-focus-within/number-input:opacity-100 group-hover/number-input:opacity-100">
            <div
              class="cursor-pointer rounded-sm bg-lt hover:bg-md"
              @mousedown="startIncrement($event)"
              @mouseup="stopAction"
              @mouseleave="stopAction"
              @keydown.enter="startIncrement($event)"
              @focusout="stopAction">
              <ChevronUpIcon
                :class="{
                  'size-3': !isChipMode,
                  'size-2.5': isChipMode,
                }" />
            </div>

            <div
              class="cursor-pointer rounded-sm bg-lt hover:bg-md"
              @mousedown="startDecrement($event)"
              @mouseup="stopAction"
              @mouseleave="stopAction"
              @keydown.enter="startDecrement($event)"
              @focusout="stopAction">
              <ChevronDownIcon
                :class="{
                  'size-3': !isChipMode,
                  'size-2.5': isChipMode,
                }" />
            </div>
          </div>
        </div>
      </div>
    </Tooltip>
  </Tooltip>
</template>
